import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TestProvider } from '../../providers/test-provider';
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { combineLatestWith, Observable, Subject, Subscription } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { map, shareReplay, tap, combineLatest } from 'rxjs/operators';
import { BorgScaleFormComponent } from './borg-scale-form/borg-scale-form';
import { MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule, MatLabel } from "@angular/material/form-field";
import { TranslateModule } from "@ngx-translate/core";
import { MatInputModule } from "@angular/material/input";
import { AsyncPipe, NgForOf, NgIf } from "@angular/common";
import { MatSelectModule } from "@angular/material/select";
import { MatButton, MatIconButton, MatMiniFabButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIcon } from "@angular/material/icon";

@Component({
  selector: 'new-test-form',
  templateUrl: './new-test-form.component.html',
  styleUrls: ['./new-test-form.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatLabel,
    TranslateModule,
    MatInputModule,
    NgIf,
    MatSelectModule,
    AsyncPipe,
    NgForOf,
    MatIconButton,
    MatTooltip,
    MatIcon,
    MatMiniFabButton,
    MatButton,
  ],
  standalone: true
})
export class NewTestFormComponent implements OnInit, AfterViewInit, OnDestroy {

  public form: FormGroup;
  public isSubmitting = false;
  public minDate: any;
  public maxDate: any;
  public startDate = new Date();
  public units$: Observable<any> = new Observable<any>();

  public readonly NAME_MAX_LENGTH: number = 100;
  private subscriptions: Subscription[] = [];

  public get metrics() : any {
    return this.form.get("metrics") as FormArray
  }

  public get isEditForm() : boolean {
    return !!(this.data.test);
  }
  public testId: number | undefined; // used when editing existing test

  protected borgScales: any[] = [];
  private borgScales$ = new Subject<any[]>();
  public borgMetrics$: Observable<any[]> = new Observable<any[]>();

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<NewTestFormComponent>,
    private fb: FormBuilder,
    private toaster: MatSnackBar,
    private utils: UtilsProvider,
    private testProvider: TestProvider,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.form = this.fb.group({
      name_en: new FormControl('', [Validators.required, Validators.maxLength(this.NAME_MAX_LENGTH)]),
      name_fr: new FormControl('', [Validators.required, Validators.maxLength(this.NAME_MAX_LENGTH)]),
      metrics: this.fb.array([]),
      borgMetric: new FormControl(null, [Validators.required]),
    });
  }

  public ngOnInit(): void {
    this.units$ = this.testProvider.getUnits().pipe(
      tap(units => console.log('Units:', units)),  // Log units for debugging
      shareReplay()
    );

    this.borgMetrics$ = this.borgScales$.pipe(
      tap(scales => console.log('Borg scales:', scales)),  // Log scales for debugging
      map(scales => {
        let metrics = scales.map((scale, i) => {
          if (scale !== undefined) {
            return {
              index: i,
              value: this.metrics.controls[i].value,
            }
          }
          return scale;
        });
        return metrics.filter(e => e !== undefined);
      }),
      combineLatestWith(this.units$),
      map(([metrics, units]) => {
        return metrics.map(metric => {
          const unit = units.find((e: any) => e.id == metric.value.unit);
          return { index: metric.index, metric: metric.value, unit: unit };
        });
      }),
      tap(finalMetrics => console.log('Final borg metrics:', finalMetrics))  // Log final metrics for debugging
    );

    this.form.controls['metrics'].valueChanges.subscribe(value => {
      this.borgScales$.next(this.borgScales);
    });

    let testId: number | undefined = undefined;
    if (this.isEditForm) {
      testId = this.data.test.id;
    } else if (this.data.originalTest) {
      testId = this.data.originalTest.id;
    }
    if (testId) {
      this.testProvider.get(testId).subscribe((test) => {
        if (this.isEditForm) {
          this.testId = test.id;
        } else {
          test.translations = test.translations.map((translation: any) => {
            translation.name += '(2)';
            return translation;
          });
        }

        const metrics = test.metrics?.map((metric: any) => {
          const metricTranslation = (langId: any) => metric.translations.find((m: any) => m.lang_id == langId);
          let borgScale = undefined;
          this.metrics.push(this.createMetric());
          if (metric.borg_bounds_m) {
            borgScale = {
              invertedScale: metric.borg_score_lowest,
              scaleMale: metric.borg_bounds_m,
              scaleFemale: metric.borg_bounds_f,
            };
          }
          this.borgScales.push(borgScale);
          return {
            name_en: metricTranslation(1).name,
            name_fr: metricTranslation(2).name,
            unit: metric.test_unit_id,
          };
        });
        this.form.controls['metrics'].patchValue(metrics);
        this.subscriptions.push(this.borgMetrics$.subscribe());
        this.borgScales$.next(this.borgScales);

        const testTranslation = (langId: any) => test.translations.find((t: any) => t.lang_id == langId);
        const borgMetricIdx = test.metrics.findIndex((m: any) => m.id == test.borg_metric_id);

        this.form.patchValue({
          name_en: testTranslation(1).name,
          name_fr: testTranslation(2).name,
          borgMetric: borgMetricIdx
        });

        this.form.controls['borgMetric'].markAsTouched();
        this.form.controls['metrics'].markAsTouched();
      });
    } else {
      this.addMetric();
    }
  }

  ngAfterViewInit(): void {}

  public ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public submitForm() {
    this.isSubmitting = true;

    if (this.form.valid) {
      let test = this.form.value;
      test.metrics = test.metrics.map((metric: any, i: any) => {
        metric['borgScale'] = this.borgScales[i];
        return metric;
      });
      if (this.isEditForm) {
        test.id = this.testId;
      }

      this.testProvider.saveTest(
        this.data.organization,
        this.data.athlete,
        test
      ).subscribe(test => {
        this.dialogRef.close(test);
      });
    } else {
      this.form.markAllAsTouched();
      this.isSubmitting = false;
    }
  }

  private createMetric(): FormGroup {
    return this.fb.group({
      name_fr: ['', Validators.required],
      name_en: ['', Validators.required],
      unit: ['', Validators.required],
    });
  }

  public removeMetric(i: number) {
    this.metrics.removeAt(i);
    this.borgScales.splice(i, 1);
    this.borgScales$.next(this.borgScales);
  }

  public addMetric() {
    this.metrics.push(this.createMetric());
    this.borgScales.push(undefined);
    this.borgScales$.next(this.borgScales);
  }

  public editBorg(i: number) {
    const unitId = this.metrics.controls[i].value.unit;
    const unit$ = this.units$.pipe(map((units: []) => {
      return units.find((e: any) => e.id == unitId);
    }));
    //this.metrics.controls[i].markAsTouched();
    this.subscriptions.push(this.dialog.open(BorgScaleFormComponent, {
      data: {
        unit$: unit$,
        data: this.borgScales[i],
      }
    }).afterClosed()
      .subscribe(response => {
        if (response.edited) {
          this.borgScales[i] = response.data;
          this.borgScales$.next(this.borgScales);
        }
      }));
  }

  get f() {
    return this.form.controls;
  }
}
